import { X } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

interface ModalShellProps {
  onClose: () => void;
  /** Used in aria-labels: "Dismiss {ariaContext}" / "Close {ariaContext}". */
  ariaContext: string;
  /** id of the h2 inside children, wired to aria-labelledby. */
  labelledBy: string;
  /** Replaces the card's layout classes. Defaults to the padded legacy card. */
  cardClassName?: string;
  /** "corner" keeps the absolutely positioned X. "none" means the caller
   *  renders <ModalCloseButton> inside its own header row. */
  closeAffordance?: "corner" | "none";
  children: ReactNode;
}

const DEFAULT_CARD =
  "relative max-h-[90vh] w-full max-w-[380px] overflow-y-auto rounded-2xl bg-[var(--bg-modal)] p-6 shadow-xl";

/** The asset modals' card: same frame as the default, on the lighter surface
 *  and unpadded so each modal lays out its own header/body/footer bands. */
export const ASSET_MODAL_CARD =
  "relative max-h-[90vh] w-full max-w-[380px] overflow-y-auto rounded-2xl bg-[var(--bg-default)] shadow-xl";

/** The dismiss control, extracted so the "Close {ariaContext}" label that tests
 *  assert has exactly one source. */
export function ModalCloseButton({
  onClose,
  ariaContext,
  className = "",
  size = 22,
}: {
  onClose: () => void;
  ariaContext: string;
  className?: string;
  size?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={`Close ${ariaContext}`}
      className={`shrink-0 text-white/80 transition-colors hover:text-white ${className}`}
    >
      <X size={size} weight="bold" />
    </button>
  );
}

export function ModalShell({
  onClose,
  ariaContext,
  labelledBy,
  cardClassName = DEFAULT_CARD,
  closeAffordance = "corner",
  children,
}: ModalShellProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Portaled: under a filtered or transformed ancestor, the fixed backdrop
  // would cover only that ancestor instead of the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md"
      style={{
        backgroundColor:
          "color-mix(in srgb, var(--bg-default-overlay) 50%, transparent)",
      }}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Dismiss ${ariaContext}`}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div
        className={cardClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        {closeAffordance === "corner" && (
          <ModalCloseButton
            onClose={onClose}
            ariaContext={ariaContext}
            className="absolute right-4 top-4"
          />
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
